import React, { useState, useEffect, useRef } from 'react';
import { X, Lock, AlertTriangle } from 'lucide-react';

/**
 * Password-gated confirmation modal.
 * Used for sensitive operations (archive/delete jobs, etc.).
 *
 * Props:
 *  - open: bool
 *  - onClose(): close handler
 *  - onConfirm({ password, reason }): called when submit clicked
 *  - title: modal heading
 *  - description: helper sentence under title
 *  - confirmLabel: action label, e.g. "Archive Job" or "Delete Permanently"
 *  - tone: "danger" | "warning"  (danger = red, warning = amber)
 *  - requireReason: bool — force user to type a reason
 *  - busy: bool — disables submit while parent is requesting
 *  - error: optional string to render below inputs
 */
export default function PasswordConfirmModal({
  open,
  onClose,
  onConfirm,
  title = 'Confirm action',
  description = 'Enter your password to continue.',
  confirmLabel = 'Confirm',
  tone = 'danger',
  requireReason = false,
  busy = false,
  error = '',
}) {
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPassword('');
      setReason('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  if (!open) return null;

  const palette = tone === 'danger'
    ? { ring: 'ring-red-500', bg: 'bg-red-600 hover:bg-red-700', icon: 'text-red-600', tint: 'bg-red-50' }
    : { ring: 'ring-amber-500', bg: 'bg-amber-600 hover:bg-amber-700', icon: 'text-amber-600', tint: 'bg-amber-50' };

  const submit = (e) => {
    e?.preventDefault?.();
    if (!password.trim()) return;
    if (requireReason && !reason.trim()) return;
    onConfirm({ password: password.trim(), reason: reason.trim() });
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="password-confirm-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        <div className={`px-6 py-4 flex items-start gap-3 ${palette.tint} border-b border-slate-200`}>
          <AlertTriangle className={`w-6 h-6 ${palette.icon} mt-0.5 flex-shrink-0`} />
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-slate-900">{title}</h3>
            <p className="text-sm text-slate-600 mt-0.5">{description}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 transition"
            aria-label="Close"
            data-testid="password-confirm-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {requireReason && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Reason <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Briefly explain why (logged in audit trail)"
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400"
                data-testid="password-confirm-reason"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Your password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={inputRef}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter current password"
                className={`w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 ${palette.ring}`}
                autoComplete="current-password"
                data-testid="password-confirm-input"
              />
            </div>
            {error && (
              <p className="text-xs text-red-600 mt-1.5" data-testid="password-confirm-error">
                {error}
              </p>
            )}
          </div>
        </div>

        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200 rounded-lg transition disabled:opacity-50"
            data-testid="password-confirm-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || !password.trim() || (requireReason && !reason.trim())}
            className={`px-4 py-2 text-sm font-semibold text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed ${palette.bg}`}
            data-testid="password-confirm-submit"
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
